import { z } from "zod";
import { MUSCLE_SLUGS } from "../bodymap.js";

// Per-user, per-muscle moving baseline of training volume. Lives in the
// single-table DDB under pk=USER#<sub>, sk=BASELINE#muscle#<slug>.
// Recomputed by the SessionResult Lambda on every session-write so the
// Progress dashboard's Tier 1 "+60% vs 28d avg" alert can fire without
// re-scanning all of the user's history at read time.
//
// The three windows serve distinct roles:
//   - avg7dKg     → short-term load (acute fatigue signal)
//   - avg28dKg    → medium-term baseline (chronic load comparator)
//   - baselineKg  → 90-day trimmed mean, slow-drifting reference
//                   (excludes top + bottom decile so a single PR
//                    session doesn't lift the bar; the alert compares
//                    avg28dKg against this).
//
// ⚠️ No severity is derived from these rows any more (PG-8): the panel that
// scored them is gone, and the thresholds it used had no source. The row is
// still WRITTEN on every SessionResult, and is still the only per-muscle
// history the product stores — what reads it next is PG-8's second slice.
export const MuscleBaseline = z.object({
  userSub: z.string().min(1).max(128),
  muscleSlug: z.enum(MUSCLE_SLUGS),
  avg7dKg: z.number().min(0),
  avg28dKg: z.number().min(0),
  // PG-5: the 28d mean in the SAME unit as baselineKg (kg per TRAINED day,
  // not per calendar day), so `current / baseline` means what the 1.30/1.60
  // thresholds assume. avg28dKg is kept unchanged beside it because rows are
  // rewritten lazily — see the note in apps/api/src/lib/muscleBaselines.ts.
  // Optional: a row written before PG-5 does not carry it, and a reader that
  // finds it missing must say "not enough data", never "all clear".
  avg28dPerTrainedDayKg: z.number().min(0).optional(),
  baselineKg: z.number().min(0),
  // Total number of distinct days that contributed to baselineKg.
  // Drives a "warming up" badge in the UI: until sampleCountDays ≥ 28
  // we render the baseline as informational only, not alert-grade.
  sampleCountDays: z.number().int().min(0),
  // Last time the baseline writer touched this row. ISO-8601 UTC, ms.
  lastUpdatedAt: z.iso.datetime(),
});
export type MuscleBaseline = z.infer<typeof MuscleBaseline>;
